import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Flex, Form, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import Counter from "ui/Counter"
import { getAdjustedRates, getMultiDayBooking, getProgressivePricing } from "utils/widgetBookingPricing"
import { MIN_PRICE_SAVING } from "../constants"
import { GUEST_ALLOWED_FLAGS, getFirstAllowedGuestType, sumGuestCounts } from "../helpers/guestTotals"
import styles from "./BookingForm.module.css"

const ALL_GUEST_FIELDS = [
  { key: "adults", rateKey: "pricePerPerson" },
  { key: "children", rateKey: "pricePerChild" },
  { key: "infants", rateKey: "pricePerInfant" },
  { key: "passengers", rateKey: "pricePerPassenger" }
].map((field) => ({ ...field, allowedFlag: GUEST_ALLOWED_FLAGS[field.key] }))

// One guest type's price for the booking: "No Charge" when free, the price before day/seasonal
// adjustments struck through when they lowered it (Figma 7287:214789).
export const GuestPrice = ({ rate, baseRate }) => {
  const { t } = useTranslation()
  if (!Number(rate)) return <span className={styles.guestPrice}>{t("booking.guest_counter.no_charge")}</span>
  const isDiscounted = Number(baseRate) - Number(rate) > MIN_PRICE_SAVING
  return (
    <Flex gap="var(--spacing-xxs)" className={styles.guestPrice}>
      {isDiscounted && <span className={styles.guestPriceStruck}>{numberToCurrency(baseRate)}</span>}
      <span>{numberToCurrency(rate)}</span>
    </Flex>
  )
}

GuestPrice.propTypes = {
  rate: PropTypes.number,
  baseRate: PropTypes.number
}

GuestPrice.defaultProps = {
  rate: 0,
  baseRate: 0
}

// A Form.Item control: the "−  4  +" stepper for one guest type.
const GuestCounter = ({ value, onChange, label, min, isDisabled, isAtMax }) => {
  const { t } = useTranslation()
  const count = Number(value) || 0
  return (
    <Counter
      value={count}
      decreaseLabel={t("booking.guest_counter.fewer", { type: label })}
      increaseLabel={t("booking.guest_counter.more", { type: label })}
      isDecreaseDisabled={isDisabled || count <= min}
      isIncreaseDisabled={isDisabled || isAtMax}
      onChange={onChange}
    />
  )
}

GuestCounter.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  label: PropTypes.string.isRequired,
  min: PropTypes.number,
  isDisabled: PropTypes.bool,
  isAtMax: PropTypes.bool
}

GuestCounter.defaultProps = {
  value: 0,
  onChange: () => {},
  min: 0,
  isDisabled: false,
  isAtMax: false
}

// Guests of a private booking (Figma 5794:87840): a counter per guest type with its price for the
// date (a multi-day trip's price for the whole trip), the allowed range beside the title.
const GuestCountSection = ({
  form,
  tripPrice,
  onChange,
  minGuests,
  maxGuests,
  bookingSelections,
  isDisabled = false,
  experience,
  isPriceShown = true
}) => {
  const { t } = useTranslation()
  const mainCounts = Form.useWatch("main", form)
  const totalGuests = sumGuestCounts(mainCounts)
  const isAtMax = maxGuests != null && maxGuests > 0 && totalGuests >= maxGuests
  const isFlatRate = tripPrice?.pricing_type === "charge_flat_rate"
  const date = bookingSelections?.date
  const endDate = bookingSelections?.endDate
  const { rates, baseRates } = useMemo(() => {
    const priceData = tripPrice || {}
    const multiDay = getMultiDayBooking({ experience, priceData, date, endDate })
    return {
      rates: getAdjustedRates({
        priceData,
        date,
        multiDay,
        dayAdjustments: experience?.day_adjustments,
        seasonalAdjustments: experience?.seasonal_adjustments,
        availableDateRanges: experience?.available_date_ranges,
        progressivePricing: getProgressivePricing(experience)
      }),
      baseRates: getAdjustedRates({ priceData, date, multiDay })
    }
  }, [tripPrice, experience, date, endDate])

  // First guest type that is not explicitly disabled — receives the min-guest default.
  const firstAllowedField = getFirstAllowedGuestType(tripPrice)
  const defaultMinCount = minGuests && minGuests > 0 ? minGuests : 1
  // New Booking's Total Guests, set before the guest box shows, seeds the first type (Edit Booking
  // has no such field, so it seeds the minimum).
  const seedCount = Math.max(defaultMinCount, Number(form.getFieldValue("totalGuests")) || 0)

  // Whether the user (or the saved booking) already has a guest count. Navigating to step 2 and back
  // remounts this component; without this guard the effects below would re-seed the minimum over the
  // existing selection (e.g. picking the previous trip's higher minimum), so anchor to the persisted
  // selection instead of the transient form field.
  const savedMain = bookingSelections?.counts?.main
  const hasSelection = !!savedMain && Object.values(savedMain).some((v) => Number(v) > 0)

  useEffect(() => {
    // On mount (including a step-2 → step-1 remount), restore the persisted counts so the saved/
    // selected values are shown rather than the seeded minimum.
    if (hasSelection) {
      form.setFieldsValue({ main: savedMain })
      onChange?.(savedMain)
    } else {
      onChange?.(form.getFieldValue("main") || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync the min default once minGuests / tripPrice resolves — but never over an existing selection.
  useEffect(() => {
    if (hasSelection) return
    const current = form.getFieldValue(["main", firstAllowedField])
    if (current == null || current < defaultMinCount) {
      form.setFieldValue(["main", firstAllowedField], seedCount)
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMinCount, firstAllowedField])

  // Zero out guest types the selected price doesn't allow — but only while the counts are being
  // chosen. When they are locked (a completed trip, or a recovered request) they record who actually
  // went / already paid; a price that has since stopped allowing children must not erase the child
  // who was on the trip, and the zeroed value would otherwise flow into the saved booking.
  useEffect(() => {
    if (!tripPrice || isDisabled) return
    const updates = {}
    ALL_GUEST_FIELDS.forEach(({ key, allowedFlag }) => {
      if (key !== firstAllowedField && tripPrice[allowedFlag] === false) {
        updates[key] = 0
      }
    })
    if (Object.keys(updates).length > 0) {
      form.setFieldsValue({ main: { ...form.getFieldValue("main"), ...updates } })
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPrice?.id])

  const handleChange = () => {
    setTimeout(() => {
      const mainCounts = form.getFieldValue("main") || {}
      onChange?.(mainCounts)
      form.validateFields([["main", firstAllowedField], "guestTotal"]).catch(() => {})
    }, 0)
  }

  const validateGuestTotal = () => {
    const main = form.getFieldValue("main") || {}
    const total = Object.values(main).reduce((sum, v) => sum + (Number(v) || 0), 0)
    if (maxGuests != null && total > maxGuests) {
      return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuests })))
    }
    if (minGuests != null && total < minGuests) {
      return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
    }
    return Promise.resolve()
  }

  return (
    <div className={styles.guestSection}>
      <Flex align="center" gap="var(--spacing-s)" className={styles.guestTitle}>
        <span>
          {t("booking.guest_counter.title")}
          <span className={styles.requiredMark}> *</span>
        </span>
        {maxGuests > 0 && (
          <span className={styles.guestLimits}>{t("booking.guest_counter.limits", { min: minGuests || 1, max: maxGuests })}</span>
        )}
      </Flex>
      <div className={styles.guestBox}>
        {ALL_GUEST_FIELDS.map(({ key, allowedFlag, rateKey }) => {
          // isDisabled freezes every type at once (recovery: the guest count is already paid for);
          // the per-type flag still applies on top of it.
          const disabled = isDisabled || (tripPrice ? tripPrice[allowedFlag] === false : false)
          const isFirstAllowed = key === firstAllowedField
          const label = t(`booking.guest_counter.types.${key}`)
          return (
            <Flex key={key} align="flex-start" justify="space-between" gap="var(--spacing-xs)" className={styles.guestRow}>
              <Flex vertical>
                <Flex align="center" gap="var(--spacing-xs)">
                  <span className={styles.guestLabel}>{label}</span>
                  {key !== "passengers" && (
                    <Tooltip title={t(`booking.guest_counter.hints.${key}`)}>
                      <InfoCircleOutlined className={styles.guestInfoIcon} aria-label={t(`booking.guest_counter.hints.${key}`)} />
                    </Tooltip>
                  )}
                </Flex>
                {isPriceShown && !isFlatRate && <GuestPrice rate={rates[rateKey]} baseRate={baseRates[rateKey]} />}
              </Flex>
              <Form.Item
                name={["main", key]}
                initialValue={isFirstAllowed ? seedCount : 0}
                className={styles.guestCounterItem}
                rules={
                  !disabled && isFirstAllowed
                    ? [{ required: true, type: "number", min: 1, message: t("booking.error_messages.adults_required") }]
                    : []
                }
              >
                <GuestCounter
                  label={label}
                  min={isFirstAllowed ? 1 : 0}
                  isDisabled={disabled}
                  isAtMax={isAtMax}
                  // Form.Item stores the count, then calls this.
                  onChange={() => handleChange()}
                />
              </Form.Item>
            </Flex>
          )
        })}
      </div>
      <Form.Item name="guestTotal" rules={[{ validator: validateGuestTotal }]} className={styles.guestTotalItem} />
    </div>
  )
}

GuestCountSection.propTypes = {
  form: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  tripPrice: PropTypes.shape({
    adults_allowed: PropTypes.bool,
    children_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    passengers_allowed: PropTypes.bool,
    pricing_type: PropTypes.string
  }),
  onChange: PropTypes.func,
  minGuests: PropTypes.number,
  maxGuests: PropTypes.number,
  bookingSelections: PropTypes.object,
  // The experience, for its day/seasonal adjustments and progressive pricing.
  experience: PropTypes.object,
  // Each guest type's price beside its counter (New Booking).
  isPriceShown: PropTypes.bool
}

export default GuestCountSection
